import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UpgradeDialog } from "@/components/waggle/UpgradeDialog";
import { onOutOfActions, type OutOfActionsInfo } from "@/lib/outOfActions";
import { USAGE_KEY } from "@/lib/plan";

// One upgrade dialog for the whole app, so any page can open it: the sidebar
// and Billing settings via openUpgrade(), and the generation request layer
// when the server refuses for lack of AI actions.
interface UpgradeContextValue {
  openUpgrade: () => void;
}

const UpgradeContext = createContext<UpgradeContextValue>({ openUpgrade: () => {} });

export function UpgradeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [outOfActions, setOutOfActions] = useState<OutOfActionsInfo | null>(null);

  const openUpgrade = useCallback(() => {
    setOutOfActions(null);
    setOpen(true);
  }, []);

  useEffect(
    () =>
      onOutOfActions((info) => {
        setOutOfActions(info);
        setOpen(true);
        // The meter is stale if it let the user get this far.
        void queryClient.invalidateQueries({ queryKey: USAGE_KEY });
      }),
    [queryClient],
  );

  const value = useMemo(() => ({ openUpgrade }), [openUpgrade]);

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <UpgradeDialog open={open} onOpenChange={setOpen} outOfActions={outOfActions} />
    </UpgradeContext.Provider>
  );
}

export function useUpgradeDialog() {
  return useContext(UpgradeContext);
}
